import React, { useCallback, useEffect, useState } from 'react';
import { api, mutate } from '../shared/api';
import { API_BASE } from '../shared/constants';
import { formatMoney, formatDate } from '../shared/formatting';
import { DateInput } from '../shared/DateInput';

interface CompanyRow {
    company_id: number;
    company_code: string;
    company_name: string;
    available_cash: number;
    opening: number;
    movements: number;
    selected_ready: number;
    selected_ready_count: number;
    selected_hitno: number;
    selected_hitno_count: number;
    selected_total: number;
    runway_after: number;
    batches: { id: number; batch_date: string; status: string; total_amount: number; title: string | null; locked_at: string | null }[];
}

interface BoardPayload {
    as_of: string;
    companies: CompanyRow[];
    gold: { due_today: number; due_horizon: number; overdue: number; horizon_days: number };
    forecast: { date: string; outflow_selected: number; gold: number }[];
    is_lock_day: boolean;
}

interface Candidate {
    needs_second_signature?: boolean;
    second_signed?: boolean;
    obligation_id: number;
    document_number: string;
    due_date: string | null;
    amount: number;
    counterparty_name: string | null;
    order_code: string | null;
    selection_status: string;
    pay_date: string | null;
    is_urgent: boolean;
}

const STATUS_COLOR: Record<string, string> = { draft: '#5a5a66', locked: '#3fae6b', authorized: '#7a5ad6', executed: '#1f7d4d', cancelled: '#7a3a2a' };

export function PaymentBatchesModule({ token, locale, onBack, embedded }: { token: string | null; locale: 'hr' | 'en'; onBack: () => void; embedded?: boolean }) {
    const [board, setBoard] = useState<BoardPayload | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [builderCo, setBuilderCo] = useState<CompanyRow | null>(null);
    const [detailId, setDetailId] = useState<number | null>(null);
    const [showMatrix, setShowMatrix] = useState(false);
    const [sortKey, setSortKey] = useState<'company_code' | 'available_cash' | 'selected_ready' | 'selected_hitno' | 'selected_total' | 'runway_after'>('company_code');
    const [sortDir, setSortDir] = useState<1 | -1>(1);
    const sortBy = (k: typeof sortKey) => { if (sortKey === k) { setSortDir((d) => (d === 1 ? -1 : 1)); } else { setSortKey(k); setSortDir(k === 'company_code' ? 1 : -1); } };
    const sArrow = (k: typeof sortKey) => sortKey === k ? (sortDir === 1 ? ' ▲' : ' ▼') : '';
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setBoard(await api<BoardPayload>('/payment-batches/board', token));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { void load(); }, [load]);

    return (
        <div style={{ padding: '18px 20px', color: '#f2f0ec' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                {!embedded ? <button onClick={onBack} style={btnGhost}>← {t('Natrag', 'Back')}</button> : null}
                <h2 style={{ margin: 0, fontSize: 20 }}>🗓️ {t('CF nalozi (batch)', 'CF batches')}</h2>
                {board ? <span style={{ opacity: 0.6, fontSize: 13 }}>{t('Na dan', 'As of')} {formatDate(board.as_of, locale)}</span> : null}
                <button onClick={() => setShowMatrix(true)} style={{ ...btnGhost, marginLeft: 'auto' }}>⚙ {t('Ovlasti', 'Authority')}</button>
                <button onClick={() => void load()} style={btnGhost}>↻ {t('Osvježi', 'Refresh')}</button>
            </div>

            {board ? (
                <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ background: board.is_lock_day ? '#1e3327' : '#2a2c31', border: `1px solid ${board.is_lock_day ? '#2f7d4d' : '#7a5a2a'}`, borderRadius: 8, padding: '6px 12px', fontSize: 13 }}>
                        {board.is_lock_day ? t('✓ Dan zaključavanja (uto/čet)', '✓ Lock day (Tue/Thu)') : t('Nije dan zaključavanja (uto/čet)', 'Not a lock day (Tue/Thu)')}
                    </span>
                    <span style={{ ...goldChip }}>🪙 {t('Zlato danas', 'Gold today')}: {formatMoney(board.gold.due_today, locale)}</span>
                    <span style={{ ...goldChip }}>🪙 {t('Zlato', 'Gold')} +{board.gold.horizon_days}d: {formatMoney(board.gold.due_horizon, locale)}</span>
                    {board.gold.overdue > 0 ? <span style={{ ...goldChip, borderColor: '#b0341f', color: '#c0392b' }}>🪙 {t('Dospjelo', 'Overdue')}: {formatMoney(board.gold.overdue, locale)}</span> : null}
                </div>
            ) : null}

            {error ? <div style={{ background: '#fdecec', color: '#b3261e', padding: 10, borderRadius: 8, marginBottom: 12 }}>{error}</div> : null}
            {loading ? <div style={{ opacity: 0.7 }}>{t('Učitavam…', 'Loading…')}</div> : null}

            {!loading && board ? (
                <div style={{ overflowX: 'auto', border: '1px solid #33363c', borderRadius: 10, marginBottom: 18 }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#2a2c31', textAlign: 'left' }}>
                                {([
                                    [t('Tvrtka', 'Company'), 'company_code', false], [t('Raspoloživo', 'Available'), 'available_cash', true], [t('PLATI', 'PAY'), 'selected_ready', true],
                                    [t('HITNO', 'URGENT'), 'selected_hitno', true], [t('Odabrano', 'Selected'), 'selected_total', true], [t('Runway', 'Runway'), 'runway_after', true],
                                    [t('Batch-evi', 'Batches'), null, false], ['', null, false],
                                ] as [string, typeof sortKey | null, boolean][]).map(([h, k, right], i) => (
                                    <th key={i} style={{ ...th, ...(right ? { textAlign: 'right' as const } : {}), ...(k ? { cursor: 'pointer', userSelect: 'none' } : {}) }} onClick={k ? () => sortBy(k) : undefined}>{h}{k ? sArrow(k) : ''}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[...board.companies].sort((a, b) => {
                                const av = a[sortKey], bv = b[sortKey];
                                if (typeof av === 'number' || typeof bv === 'number') return sortDir * ((Number(av ?? 0)) - (Number(bv ?? 0)));
                                return sortDir * String(av ?? '').localeCompare(String(bv ?? ''));
                            }).map((c) => (
                                <tr key={c.company_id} style={{ borderTop: '1px solid #33363c' }}>
                                    <td style={{ ...td, fontWeight: 600 }}>{c.company_code}<div style={{ fontSize: 10, opacity: 0.5 }}>{c.company_name}</div></td>
                                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(c.available_cash, locale)}</td>
                                    <td style={{ ...td, textAlign: 'right', color: c.selected_ready > 0 ? '#1f7d4d' : undefined }}>{formatMoney(c.selected_ready, locale)}<span style={{ fontSize: 10, opacity: 0.5 }}> ({c.selected_ready_count})</span></td>
                                    <td style={{ ...td, textAlign: 'right', color: c.selected_hitno > 0 ? '#c0392b' : undefined }}>{formatMoney(c.selected_hitno, locale)}<span style={{ fontSize: 10, opacity: 0.5 }}> ({c.selected_hitno_count})</span></td>
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{formatMoney(c.selected_total, locale)}</td>
                                    <td style={{ ...td, textAlign: 'right', color: c.runway_after < 0 ? '#c0392b' : '#1f7d4d', fontWeight: 600 }}>{formatMoney(c.runway_after, locale)}</td>
                                    <td style={td}>
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {c.batches.length === 0 ? <span style={{ opacity: 0.4 }}>—</span> : c.batches.map((b) => (
                                                <button key={b.id} onClick={() => setDetailId(b.id)} title={`${b.title ?? ''} ${formatMoney(b.total_amount, locale)}`} style={{ background: STATUS_COLOR[b.status] ?? '#444', color: '#fff', padding: '2px 7px', borderRadius: 6, fontSize: 11, border: 'none', cursor: 'pointer' }}>
                                                    {formatDate(b.batch_date, locale)} · {b.status}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                    <td style={td}>
                                        <button disabled={c.selected_total <= 0} onClick={() => setBuilderCo(c)} style={{ ...miniBtn('#3fae6b'), opacity: c.selected_total <= 0 ? 0.4 : 1 }}>+ {t('Batch', 'Batch')}</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : null}

            {!loading && board && board.forecast.length > 0 ? (
                <div style={{ border: '1px solid #33363c', borderRadius: 10, padding: 14, marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13 }}>{t('15-dnevni forecast (odabrana plaćanja + zlato)', '15-day forecast (selected payments + gold)')}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {board.forecast.map((f) => (
                            <div key={f.date} style={{ background: '#24262b', border: '1px solid #33363c', borderRadius: 8, padding: '6px 10px', minWidth: 96 }}>
                                <div style={{ fontSize: 11, opacity: 0.7 }}>{formatDate(f.date, locale)}</div>
                                {f.outflow_selected > 0 ? <div style={{ fontSize: 12 }}>💸 {formatMoney(f.outflow_selected, locale)}</div> : null}
                                {f.gold > 0 ? <div style={{ fontSize: 12, color: '#d6b15a' }}>🪙 {formatMoney(f.gold, locale)}</div> : null}
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

            {builderCo ? (
                <BatchBuilder company={builderCo} token={token} locale={locale} onClose={() => setBuilderCo(null)} onDone={() => { setBuilderCo(null); void load(); }} setError={setError} />
            ) : null}
            {detailId ? (
                <BatchDetail batchId={detailId} token={token} locale={locale} onClose={() => setDetailId(null)} onChanged={() => { void load(); }} setError={setError} />
            ) : null}
            {showMatrix ? (
                <MatrixModal token={token} locale={locale} onClose={() => setShowMatrix(false)} setError={setError} />
            ) : null}
        </div>
    );
}

interface BatchShow {
    batch: { id: number; company_code: string; batch_date: string; status: string; total_amount: number; title: string | null; available_cash_snapshot: number | null; gold_reserve_snapshot: number | null; locked_at: string | null; authorized_at: string | null; executed_at: string | null; realized_at?: string | null };
    items: { realized_at?: string | null; realized_source?: string | null; statement_confirmed_at?: string | null; id: number; amount: number; item_kind: string; status: string; payment_request_id: number | null; document_number: string | null; due_date: string | null; counterparty_name: string | null }[];
    authorization: { dual_threshold_eur?: number; required_signatures: number; collected_signatures: number; dual_required: boolean; my_limit: number | null; i_can_sign: boolean; i_signed: boolean; signatures: { user_name: string | null; decision: string; amount_at_time: number | null; note: string | null; at: string }[] };
}

function BatchDetail({ batchId, token, locale, onClose, onChanged, setError }: {
    batchId: number; token: string | null; locale: 'hr' | 'en'; onClose: () => void; onChanged: () => void; setError: (s: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<BatchShow | null>(null);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');
    const [virmani, setVirmani] = useState<{ item_id: number; counterparty: string | null; document_number: string | null; amount: number; iban: string | null; model: string | null; reference: string | null; payload: string; barcode: string | null }[] | null>(null);
    const [genMsg, setGenMsg] = useState('');

    const load = useCallback(async () => {
        try { setData(await api<BatchShow>(`/payment-batches/${batchId}`, token)); }
        catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }, [batchId, token, setError]);
    useEffect(() => { void load(); }, [load]);

    async function destroyBatch() {
        if (!window.confirm(t('Obrisati ovaj nalog? Obveze se vraćaju u odabir.', 'Delete this batch? Invoices return to selection.'))) return;
        setBusy(true);
        try {
            await mutate(`/payment-batches/${batchId}`, token, 'DELETE', {});
            onChanged(); onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function act(kind: 'authorize' | 'reject') {
        setBusy(true);
        try {
            await mutate(`/payment-batches/${batchId}/${kind}`, token, 'POST', { note: note || null });
            await load();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function loadBarcodes() {
        try {
            const res = await api<{ barcodes: typeof virmani }>(`/payment-batches/${batchId}/virmani/barcodes`, token);
            setVirmani(res.barcodes);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }

    async function virmanAction(kind: 'generate' | 'execute' | 'reconcile' | 'realize') {
        setBusy(true);
        setGenMsg('');
        try {
            const res = await mutate<Record<string, unknown>>(`/payment-batches/${batchId}/${kind === 'generate' ? 'virmani/generate' : kind}`, token, 'POST', {});
            if (kind === 'generate') {
                const miss = typeof res.missing_count === 'number' ? res.missing_count : 0;
                setGenMsg(`✓ ${res.generated ?? 0} virmana${miss > 0 ? ` · ⚠ ${miss} bez IBAN-a` : ''}`);
                await loadBarcodes();
            } else if (kind === 'reconcile') {
                setGenMsg(`✓ ${res.matched ?? 0} uparenih s izvodom`);
            }
            await load();
            onChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function downloadExport() {
        try {
            const resp = await fetch(`${API_BASE}/payment-batches/${batchId}/virmani/export`, { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
            if (!resp.ok) throw new Error('Export failed');
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `virmani_b${batchId}.xlsx`; a.click();
            URL.revokeObjectURL(url);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={{ ...modal, width: 600 }} onClick={(e) => e.stopPropagation()}>
                {!data ? <div style={{ opacity: 0.7 }}>{t('Učitavam…', 'Loading…')}</div> : (
                    <>
                        <h3 style={{ marginTop: 0 }}>🗓️ {t('Batch', 'Batch')} #{data.batch.id} — {data.batch.company_code} · {formatDate(data.batch.batch_date, locale)}</h3>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10, fontSize: 13 }}>
                            <span style={{ background: STATUS_COLOR[data.batch.status] ?? '#444', color: '#fff', padding: '3px 10px', borderRadius: 6 }}>{data.batch.status}</span>
                            <span>{t('Ukupno', 'Total')}: <b>{formatMoney(data.batch.total_amount, locale)}</b></span>
                            {data.batch.available_cash_snapshot !== null ? <span style={{ opacity: 0.7 }}>{t('stanje@lock', 'cash@lock')}: {formatMoney(data.batch.available_cash_snapshot, locale)}</span> : null}
                        </div>

                        <div style={{ background: '#24262b', border: '1px solid #33363c', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                                {t('Ovlaštenje', 'Authorization')}: {data.authorization.collected_signatures}/{data.authorization.required_signatures} {t('potpisa', 'signatures')}
                                {data.authorization.dual_required ? <span style={{ color: '#d6a15a', marginLeft: 6 }}>({t('dupli potpis > ', 'dual sign > ')}{formatMoney(data.authorization.dual_threshold_eur ?? 0, locale)})</span> : null}
                            </div>
                            {data.authorization.signatures.length > 0 ? data.authorization.signatures.map((s, i) => (
                                <div key={i} style={{ fontSize: 12, opacity: 0.85 }}>
                                    {s.decision === 'approve' ? '✓' : '✗'} {s.user_name} {s.note ? <span style={{ opacity: 0.6 }}>— {s.note}</span> : null}
                                </div>
                            )) : <div style={{ fontSize: 12, opacity: 0.5 }}>{t('Još bez potpisa', 'No signatures yet')}</div>}
                            {data.authorization.my_limit !== null ? <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>{t('Tvoja ovlast', 'Your limit')}: {formatMoney(data.authorization.my_limit, locale)}</div> : <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>{t('Tvoja ovlast: neograničeno', 'Your limit: unlimited')}</div>}
                        </div>

                        <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #33363c', borderRadius: 8, marginBottom: 12 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <tbody>
                                    {data.items.map((it) => (
                                        <tr key={it.id} style={{ borderTop: '1px solid #33363c' }}>
                                            <td style={{ padding: 6 }}>{it.counterparty_name ?? '—'}</td>
                                            <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11 }}>{it.document_number}</td>
                                            <td style={{ padding: 6, textAlign: 'right' }}>{formatMoney(it.amount, locale)}</td>
                                            <td style={{ padding: 6 }}>{it.payment_request_id ? `virman #${it.payment_request_id}` : it.status}{it.statement_confirmed_at ? ` · ${t('potvrđeno izvodom', 'confirmed by statement')}` : it.realized_at ? ` · ${t('realizirano, čeka izvod', 'realized, awaiting statement')}` : ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {(data.batch.status === 'authorized' || data.batch.status === 'executed') ? (
                            <div style={{ background: '#24262b', border: '1px solid #33363c', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>💸 {t('Virmani', 'Wire transfers')}{genMsg ? <span style={{ marginLeft: 8, color: '#1f7d4d', fontWeight: 400 }}>{genMsg}</span> : null}</div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {data.batch.status === 'authorized' ? <button disabled={busy} onClick={() => void virmanAction('generate')} style={miniBtn('#3fae6b')}>{t('Generiraj HUB3', 'Generate HUB3')}</button> : null}
                                    <button disabled={busy} onClick={() => void downloadExport()} style={miniBtn('#3a3a46')}>⬇ {t('Export xlsx', 'Export xlsx')}</button>
                                    {!virmani ? <button disabled={busy} onClick={() => void loadBarcodes()} style={miniBtn('#3a3a46')}>{t('Prikaži kodove', 'Show codes')}</button> : null}
                                    {data.batch.status === 'authorized' ? <button disabled={busy} onClick={() => void virmanAction('execute')} style={miniBtn('#7a5ad6')}>{t('Označi izvršeno', 'Mark executed')}</button> : null}
                                    {data.batch.status === 'executed' ? <button disabled={busy} onClick={() => void virmanAction('reconcile')} style={miniBtn('#1f7d4d')}>{t('Rekonciliraj s izvodom', 'Reconcile with statement')}</button> : null}
                                    {data.batch.status === 'executed' && !data.batch.realized_at ? <button disabled={busy} onClick={() => void virmanAction('realize')} title={t('Novac je otišao. Iznos izlazi iz obveza i čeka potvrdu izvodom.', 'Money sent. Leaves obligations, awaits statement.')} style={miniBtn('#555')}>{t('Označi realizirano', 'Mark realized')}</button> : null}
                                </div>
                                {virmani && virmani.length > 0 ? (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                                        {virmani.map((v) => (
                                            <div key={v.item_id} style={{ background: '#f4efe3', border: '1px solid #33363c', borderRadius: 8, padding: 8, width: 168 }}>
                                                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 4 }}>{v.counterparty}</div>
                                                {v.barcode ? <img src={v.barcode} alt="HUB3" style={{ width: '100%', background: '#fff', borderRadius: 4 }} /> : <div style={{ fontSize: 10, opacity: 0.6, fontFamily: 'monospace', wordBreak: 'break-all' }}>{v.iban}<br />{formatMoney(v.amount, locale)} · {v.model} {v.reference}</div>}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {data.batch.status === 'locked' ? (
                            <>
                                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Napomena (opcionalno)', 'Note (optional)')} style={{ ...input, width: '100%', marginBottom: 10, boxSizing: 'border-box' }} />
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button disabled={busy} onClick={() => void destroyBatch()} style={{ ...btnGhost, color: '#c05a5a', borderColor: '#7a3a2a', marginRight: 'auto' }}>🗑 {t('Obriši', 'Delete')}</button>
                                    <button onClick={onClose} style={btnGhost}>{t('Zatvori', 'Close')}</button>
                                    <button disabled={busy} onClick={() => void act('reject')} style={{ ...btnGhost, color: '#fff', background: '#7a3a2a', borderColor: '#7a3a2a' }}>✗ {t('Odbij', 'Reject')}</button>
                                    <button disabled={busy || !data.authorization.i_can_sign} onClick={() => void act('authorize')} style={{ ...btnGhost, color: '#fff', background: data.authorization.i_can_sign ? '#1f7d4d' : '#9a9488', borderColor: data.authorization.i_can_sign ? '#1f7d4d' : '#9a9488', opacity: data.authorization.i_can_sign ? 1 : 0.6 }}>
                                        {data.authorization.i_signed ? t('Već potpisano', 'Already signed') : `✓ ${t('Ovjeri', 'Authorize')}`}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                {data.batch.status === 'draft' ? <button disabled={busy} onClick={() => void destroyBatch()} style={{ ...btnGhost, color: '#c05a5a', borderColor: '#7a3a2a', marginRight: 'auto' }}>🗑 {t('Obriši', 'Delete')}</button> : null}
                                <button onClick={onClose} style={btnGhost}>{t('Zatvori', 'Close')}</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

interface MatrixRow { id: number; company_id: number | null; company_code: string | null; user_id: number; user_name: string | null; user_email: string | null; limit_eur: number; is_backup: boolean; is_active: boolean; notes: string | null }

function MatrixModal({ token, locale, onClose, setError }: { token: string | null; locale: 'hr' | 'en'; onClose: () => void; setError: (s: string) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<MatrixRow[]>([]);
    const [companies, setCompanies] = useState<{ id: number; code: string }[]>([]);
    const [users, setUsers] = useState<{ id: number; name: string; email: string }[]>([]);
    const [dual, setDual] = useState<number | null>(null);
    const [nUser, setNUser] = useState('');
    const [nCompany, setNCompany] = useState('');
    const [nLimit, setNLimit] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api<{ rows: MatrixRow[]; companies: { id: number; code: string }[]; users: { id: number; name: string; email: string }[]; dual_threshold_eur: number }>('/payment-authorizations', token);
            setRows(res.rows); setCompanies(res.companies); setUsers(res.users); setDual(res.dual_threshold_eur);
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }, [token, setError]);
    useEffect(() => { void load(); }, [load]);

    async function add() {
        if (!nUser || !nLimit) return;
        setBusy(true);
        try {
            await mutate('/payment-authorizations', token, 'POST', { user_id: Number(nUser), company_id: nCompany ? Number(nCompany) : null, limit_eur: Number(nLimit) });
            setNUser(''); setNCompany(''); setNLimit('');
            await load();
        } catch (err) { setError(err instanceof Error ? err.message : 'Error'); } finally { setBusy(false); }
    }
    async function remove(id: number) {
        try { await mutate(`/payment-authorizations/${id}`, token, 'DELETE'); await load(); }
        catch (err) { setError(err instanceof Error ? err.message : 'Error'); }
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={{ ...modal, width: 680 }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginTop: 0 }}>⚙ {t('Matrica ovlasti plaćanja', 'Payment authorization matrix')}</h3>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 12 }}>
                    {t(`Jedan potpis ≤ ${(dual == null ? "…" : formatMoney(dual, locale))}; dupli potpis iznad. Potpisnikova ovlast mora pokriti iznos batcha.`,
                       `Single signature ≤ ${(dual == null ? "…" : formatMoney(dual, locale))}; dual above. Signer's limit must cover the batch amount.`)}
                </div>
                <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #33363c', borderRadius: 8, marginBottom: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead><tr style={{ background: '#2a2c31', textAlign: 'left' }}>{[t('Korisnik', 'User'), t('Tvrtka', 'Company'), t('Limit', 'Limit'), ''].map((h) => <th key={h} style={{ padding: 7 }}>{h}</th>)}</tr></thead>
                        <tbody>
                            {rows.length === 0 ? <tr><td colSpan={4} style={{ padding: 10, opacity: 0.5 }}>{t('Još nema ovlasti — vlasnik/superadmin ovjerava neograničeno.', 'No matrix yet — owner/superadmin authorizes unlimited.')}</td></tr> : rows.map((r) => (
                                <tr key={r.id} style={{ borderTop: '1px solid #33363c' }}>
                                    <td style={{ padding: 7 }}>{r.user_name}<div style={{ fontSize: 10, opacity: 0.5 }}>{r.user_email}</div></td>
                                    <td style={{ padding: 7 }}>{r.company_code ?? t('sve', 'all')}</td>
                                    <td style={{ padding: 7, textAlign: 'right' }}>{formatMoney(r.limit_eur, locale)}</td>
                                    <td style={{ padding: 7 }}><button onClick={() => void remove(r.id)} style={miniBtn('#7a3a2a')}>✕</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select value={nUser} onChange={(e) => setNUser(e.target.value)} style={input}>
                        <option value="">{t('Korisnik…', 'User…')}</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                    <select value={nCompany} onChange={(e) => setNCompany(e.target.value)} style={input}>
                        <option value="">{t('Sve tvrtke', 'All companies')}</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </select>
                    <input type="number" value={nLimit} onChange={(e) => setNLimit(e.target.value)} placeholder={t('Limit €', 'Limit €')} style={{ ...input, width: 120 }} />
                    <button disabled={busy || !nUser || !nLimit} onClick={() => void add()} style={{ ...btnGhost, color: '#fff', background: '#3fae6b', borderColor: '#3fae6b' }}>+ {t('Dodaj', 'Add')}</button>
                    <button onClick={onClose} style={{ ...btnGhost, marginLeft: 'auto' }}>{t('Zatvori', 'Close')}</button>
                </div>
            </div>
        </div>
    );
}

function nextThursdayIso(): string {
    const d = new Date();
    const day = d.getDay(); // 0=Sun..4=Thu
    const add = ((4 - day + 7) % 7) || 7;
    d.setDate(d.getDate() + add);
    return d.toISOString().slice(0, 10);
}

function BatchBuilder({ company, token, locale, onClose, onDone, setError }: {
    company: CompanyRow; token: string | null; locale: 'hr' | 'en'; onClose: () => void; onDone: () => void; setError: (s: string) => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [cands, setCands] = useState<Candidate[]>([]);
    const [checked, setChecked] = useState<Record<number, boolean>>({});
    const [batchDate, setBatchDate] = useState(nextThursdayIso());
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [createdId, setCreatedId] = useState<number | null>(null);
    const [allowOffDay, setAllowOffDay] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await api<{ candidates: Candidate[] }>(`/payment-batches/candidates/${company.company_id}`, token);
                setCands(res.candidates);
                setChecked(Object.fromEntries(res.candidates.map((c) => [c.obligation_id, !(c.needs_second_signature && !c.second_signed)])));
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Error');
            } finally {
                setLoading(false);
            }
        })();
    }, [company.company_id, token, setError]);

    const selectedIds = cands.filter((c) => checked[c.obligation_id]).map((c) => c.obligation_id);
    const selectedSum = cands.filter((c) => checked[c.obligation_id]).reduce((a, c) => a + c.amount, 0);

    async function create() {
        setBusy(true);
        try {
            const res = await mutate<{ batch_id: number }>('/payment-batches', token, 'POST', {
                company_id: company.company_id, batch_date: batchDate, title: title || null, obligation_ids: selectedIds,
            });
            setCreatedId(res.batch_id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Error');
        } finally {
            setBusy(false);
        }
    }

    async function lock() {
        if (!createdId) return;
        setBusy(true);
        try {
            await mutate(`/payment-batches/${createdId}/lock`, token, 'POST', { allow_off_day: allowOffDay });
            onDone();
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Error';
            if (msg.includes('utorkom') || msg.toLowerCase().includes('lock day') || msg.includes('Tue/Thu')) {
                setAllowOffDay(true);
                setError(t('Nije uto/čet — označi iznimku pa ponovo zaključaj.', 'Not Tue/Thu — confirm exception then lock again.'));
            } else {
                setError(msg);
            }
        } finally {
            setBusy(false);
        }
    }

    return (
        <div style={overlay} onClick={onClose}>
            <div style={{ ...modal, width: 640 }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginTop: 0 }}>🗓️ {t('Novi CF batch', 'New CF batch')} — {company.company_code}</h3>
                {loading ? <div style={{ opacity: 0.7 }}>{t('Učitavam…', 'Loading…')}</div> : (
                    <>
                        {!createdId ? (
                            <>
                                <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #33363c', borderRadius: 8, marginBottom: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                        <tbody>
                                            {cands.map((c) => (
                                                <tr key={c.obligation_id} style={{ borderTop: '1px solid #33363c', background: c.is_urgent ? '#fbe9e4' : undefined }}>
                                                    <td style={{ padding: 6 }}><input type="checkbox" disabled={!!c.needs_second_signature && !c.second_signed} title={c.needs_second_signature && !c.second_signed ? t('Iznad praga, čeka 2. potpis', 'Above threshold, awaiting 2nd signature') : undefined} checked={!!checked[c.obligation_id]} onChange={(e) => setChecked((p) => ({ ...p, [c.obligation_id]: e.target.checked }))} /></td>
                                                    <td style={{ padding: 6 }}>
                                                        <span style={{ background: c.selection_status === 'hitno' ? '#b0341f' : '#1f7d4d', color: '#fff', padding: '1px 6px', borderRadius: 5, fontSize: 10, fontWeight: 700 }}>{c.selection_status === 'hitno' ? t('HITNO', 'URG') : t('PLATI', 'PAY')}</span>
                                                        {c.needs_second_signature ? <span style={{ marginLeft: 4, border: '1px solid #888', padding: '0 5px', borderRadius: 5, fontSize: 10 }}>{c.second_signed ? t('2. potpis', '2nd sig') : t('čeka 2. potpis', 'needs 2nd sig')}</span> : null}
                                                    </td>
                                                    <td style={{ padding: 6, maxWidth: 180 }}>{c.counterparty_name ?? '—'}</td>
                                                    <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11 }}>{c.document_number}</td>
                                                    <td style={{ padding: 6, opacity: 0.7 }}>{c.due_date ? formatDate(c.due_date, locale) : '—'}</td>
                                                    <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{formatMoney(c.amount, locale)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                                    <label style={{ fontSize: 13 }}>{t('Dan batcha', 'Batch day')}:</label>
                                    <DateInput value={batchDate} onChange={setBatchDate} />
                                    <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('Naziv (opcionalno)', 'Title (optional)')} style={{ ...input, flex: 1 }} />
                                </div>
                                <div style={{ fontSize: 14, marginBottom: 12 }}>
                                    {t('Odabrano', 'Selected')}: <b>{selectedIds.length}</b> · {formatMoney(selectedSum, locale)}
                                    <span style={{ opacity: 0.6 }}> · {t('runway nakon', 'runway after')}: {formatMoney(company.available_cash - selectedSum, locale)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={onClose} style={btnGhost}>{t('Odustani', 'Cancel')}</button>
                                    <button disabled={busy || selectedIds.length === 0} onClick={() => void create()} style={{ ...btnGhost, color: '#fff', background: '#3fae6b', borderColor: '#3fae6b' }}>{busy ? '…' : t('Kreiraj batch (draft)', 'Create batch (draft)')}</button>
                                </div>
                            </>
                        ) : (
                            <div>
                                <div style={{ background: '#1e3327', border: '1px solid #2f7d4d', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                                    ✓ {t('Batch kreiran (draft)', 'Batch created (draft)')} #{createdId} — {formatMoney(selectedSum, locale)}
                                </div>
                                {allowOffDay ? (
                                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 12, color: '#a06a1f' }}>
                                        <input type="checkbox" checked={allowOffDay} onChange={(e) => setAllowOffDay(e.target.checked)} />
                                        {t('Zaključaj iako nije uto/čet (iznimka)', 'Lock even though not Tue/Thu (exception)')}
                                    </label>
                                ) : null}
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={onDone} style={btnGhost}>{t('Ostavi draft', 'Leave as draft')}</button>
                                    <button disabled={busy} onClick={() => void lock()} style={{ ...btnGhost, color: '#fff', background: '#7a5ad6', borderColor: '#7a5ad6' }}>{busy ? '…' : t('🔒 Zaključaj batch', '🔒 Lock batch')}</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const input: React.CSSProperties = { background: '#24262b', color: '#f2f0ec', border: '1px solid #3a3d44', borderRadius: 8, padding: '7px 10px', fontSize: 13 };
const btnGhost: React.CSSProperties = { background: '#24262b', color: '#f2f0ec', border: '1px solid #3a3d44', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
const miniBtn = (bg: string): React.CSSProperties => ({ background: bg, color: '#fff', border: 'none', borderRadius: 6, padding: '4px 9px', cursor: 'pointer', fontSize: 11, fontWeight: 600 });
const goldChip: React.CSSProperties = { background: 'rgba(201,162,39,0.12)', border: '1px solid rgba(201,162,39,0.4)', color: '#c9a227', borderRadius: 8, padding: '6px 12px', fontSize: 13 };
const th: React.CSSProperties = { padding: '9px 10px', fontWeight: 600, fontSize: 12, opacity: 0.85 };
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modal: React.CSSProperties = { background: '#24262b', border: '1px solid #3a3d44', borderRadius: 12, padding: 22, maxWidth: '94vw', color: '#f2f0ec' };
